import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["New", "Contacted", "Counselling", "Interested", "Application", "Admission", "Converted", "Not Interested", "Follow-up"];
const SOURCES = ["website", "whatsapp", "phone", "google", "social", "workshop", "referral", "walk-in", "campaign"];

export default function AdminLeads() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [filters, setFilters] = useState({ status: "", source: "", q: "", page: 1 });

  const load = () => {
    const params = { page: filters.page, limit: 20 };
    if (filters.status && filters.status !== "all") params.status = filters.status;
    if (filters.source && filters.source !== "all") params.source = filters.source;
    if (filters.q) params.q = filters.q;
    api.get("/admin/leads", { params }).then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(load, [filters.status, filters.source, filters.page]);

  const updateStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      toast.success(`Lead marked as ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-leads-page">
      <h1 className="font-display text-2xl font-bold text-white mb-1">Lead CRM</h1>
      <p className="text-xs text-slate-500 mb-8">{data.total} leads tracked across all sources.</p>

      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 bg-navy-surface border border-white/10 rounded-xl px-3">
          <Search className="w-4 h-4 text-slate-500" />
          <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search name / phone / course"
            className="bg-transparent border-0 text-white text-sm w-56 focus-visible:ring-0" data-testid="leads-search-input" />
        </form>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v, page: 1 })}>
          <SelectTrigger className="w-44 bg-navy-surface border-white/15 text-slate-200" data-testid="leads-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.source || "all"} onValueChange={(v) => setFilters({ ...filters, source: v, page: 1 })}>
          <SelectTrigger className="w-40 bg-navy-surface border-white/15 text-slate-200" data-testid="leads-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
            <SelectItem value="all">All Sources</SelectItem>
            {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-white/10 rounded-2xl overflow-x-auto" data-testid="leads-table">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/10">
              <th className="px-5 py-3.5">Lead</th>
              <th className="px-5 py-3.5">Interest</th>
              <th className="px-5 py-3.5">Type / Source</th>
              <th className="px-5 py-3.5">Date</th>
              <th className="px-5 py-3.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((l) => (
              <tr key={l.id} className="border-b border-white/5 hover:bg-navy-hover/40 transition-colors" data-testid={`lead-row-${l.id}`}>
                <td className="px-5 py-3.5">
                  <div className="text-white font-medium">{l.name}
                    {l.demo && <span className="text-[9px] text-slate-500 border border-white/15 rounded-full px-1.5 py-0.5 ml-2 uppercase">Demo</span>}
                  </div>
                  <div className="text-xs text-slate-500">{l.phone}{l.email ? ` • ${l.email}` : ""}</div>
                </td>
                <td className="px-5 py-3.5 text-slate-300">{l.course_interest || l.event_title || "—"}</td>
                <td className="px-5 py-3.5 text-slate-400 text-xs">{l.type} / {l.source}</td>
                <td className="px-5 py-3.5 text-slate-500 text-xs">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-5 py-3.5">
                  <Select value={l.status} onValueChange={(v) => updateStatus(l, v)}>
                    <SelectTrigger className="w-36 h-8 bg-navy-base border-white/15 text-slate-200 text-xs" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <div className="flex gap-2 mt-6">
          {Array.from({ length: data.pages }, (_, i) => (
            <button key={i} onClick={() => setFilters({ ...filters, page: i + 1 })}
              className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${filters.page === i + 1 ? "bg-orange-600 text-white" : "bg-navy-surface text-slate-300 border border-white/10"}`}
              data-testid={`leads-page-${i + 1}`}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}
